import { del, get, patch, post, put } from './methods';
import type { CreateRequest } from './types';

/**
 * Create an API method. This abstraction is useful to create more specific
 * API function calls with declared input/output types.
 * @example
 * const getUser = createAPIMethod<{ id: string }, { name: string }>({
 *   method: 'GET',
 *   url: '/user',
 * });
 *
 * // This has fully typed parameters and response.
 * const user = await getUser({ id: '1' });
 */
export const createRequest: CreateRequest = (opts) => async (input) => {
  let method;

  switch (opts.method) {
    case 'GET':
      method = get;
      break;
    case 'POST':
      method = post;
      break;
    case 'DELETE':
      method = del;
      break;
    case 'PUT':
      method = put;
      break;
    case 'PATCH':
      method = patch;
      break;
    default:
      method = get;
  }

  const curl = requestToCurl(
    opts.url,
    opts.method ?? 'GET',
    input,
    opts.fetchOptions?.headers as Record<string, string>,
  );

  const debugInfo = opts.debug ? { curl } : null;

  const response = await method(opts.url, input ?? {}, opts.fetchOptions ?? {});
  const status = response.status;

  // Clone the response to avoid stream lock if json fails.
  const clonedResponse = response.clone();

  try {
    const data = await response.json();
    if (debugInfo) {
      debugLog({ curl, url: opts.url, status, ok: response.ok, data, isText: false });
    }
    return { data, status, headers: response.headers, ok: response.ok };
  } catch (_) {
    const data = await clonedResponse.text();
    if (debugInfo) {
      debugLog({ curl, url: opts.url, status, ok: clonedResponse.ok, data, isText: true });
    }
    return { data, status, headers: clonedResponse.headers, ok: clonedResponse.ok };
  }
};

function requestToCurl(
  url: string,
  method: string,
  body?: unknown,
  headers?: Record<string, string>,
): string {
  let curl = [`curl -X ${method}`];

  if (headers) {
    for (const [key, value] of Object.entries(headers)) {
      curl.push(`-H '${key}: ${value}'`);
    }
  }

  if (body && method !== 'GET') {
    curl.push(`-d '${JSON.stringify(body)}'`);
  }

  curl.push(`'${url}'`);

  return curl.join(' ');
}

function debugLog(args: {
  curl: string;
  url: string;
  status: number;
  ok: boolean;
  data: unknown;
  isText: boolean;
}) {
  const { curl, url, status, ok, data, isText } = args;
  const write = (msg: string) => process.stdout.write(`${msg}\n`);
  write('\x1b[36m======================== DEBUG ========================\x1b[0m');
  write('\x1b[33mRequest (cURL):\x1b[0m');
  write(`\x1b[33m${curl}\x1b[0m`);
  write('\x1b[32mResponse:\x1b[0m');
  write(`\x1b[32m  URL: ${url}\x1b[0m`);
  write(`\x1b[32m  Status: ${status}\x1b[0m`);
  write(`\x1b[32m  OK: ${ok}\x1b[0m`);
  const pretty = isText || typeof data === 'string' ? String(data) : JSON.stringify(data, null, 2);
  write('\x1b[35m  Body:\x1b[0m');
  write(`\x1b[35m${pretty}\x1b[0m`);
  write('\x1b[36m=======================================================\x1b[0m');
}
